<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <b-form>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">Communication Paths</h5>
              <div class="d-flex gap-1">
                <b-button
                  variant="primary"
                  size="sm"
                  @click="addNewPath"
                  :disabled="isLoading || isSavingPath"
                  class="text-capitalize"
                >
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  Add Path
                </b-button>
              </div>
            </div>

            <div v-if="isLoading" class="text-center py-4">
              <b-spinner variant="primary" label="Loading paths..."></b-spinner>
              <p class="mt-2 mb-0">Loading paths...</p>
            </div>

            <b-row v-if="!isLoading">
              <b-col cols="4" class="table-column">
                <!-- Communication Paths Table -->
                <b-table
                  :items="communicationPaths"
                  :fields="pathTableFields"
                  responsive
                  striped
                  hover
                  show-empty
                  empty-text="No paths configured"
                  selectable
                  select-mode="single"
                  @row-selected="onRowSelected"
                  sort-by="path"
                  :sort-desc="false"
                  class="mb-0"
                >
                  <template #cell(path)="data">
                    {{ data.item.path }}
                  </template>

                  <template #cell(pathType)="data">
                    {{ data.item.pathTypeLabel }}
                  </template>

                  <template #cell(commType)="data">
                    {{ data.item.commTypeLabel }}
                  </template>
                </b-table>
              </b-col>

              <b-col cols="8" class="details-column">
                <!-- Selected Path Details Card -->
                <b-card v-if="selectedPath" no-body>
                  <b-card-header class="mb-0 card-header-custom pt-0">
                    <h6 class="mb-0">Communication Path Details</h6>
                  </b-card-header>
                  <b-card-body>



                    <validation-observer ref="pathFormObserver">
                      <b-form @submit.prevent="savePath">
                        <!-- Tabs for Path and Advanced -->
                         
                        <b-tabs v-model="activeTab" pills class="comm-path-tabs">
                          <b-tab title="Path" active>
                            <path-tab
                              :path-form="pathForm"
                              :is-saving="isSavingPath"
                              @activate-cellular="onActivateCellular"
                            />
                          </b-tab>
                          <b-tab title="Advanced">
                            <advanced-tab
                              :path-form="pathForm"
                              :is-saving="isSavingPath"
                            />
                          </b-tab>
                        </b-tabs>

                        <!-- Action Buttons -->
                        <b-row class="mt-3">
                          <b-col cols="12" class="d-flex justify-content-end">
                            <b-button
                              v-if="isEditingPath"
                              variant="outline-danger"
                              size="sm"
                              class="mr-1"
                              @click="deletePath"
                              :disabled="isSavingPath || isDeletingPath"
                            >
                              <b-spinner v-if="isDeletingPath" small class="mr-25" />
                              <feather-icon
                                v-else
                                icon="TrashIcon"
                                size="14"
                                class="mr-25"
                              />
                              <span>Delete</span>
                            </b-button>
                            <b-button
                              variant="primary"
                              size="sm"
                              type="submit"
                              :disabled="isSavingPath || isDeletingPath"
                            >
                              <b-spinner v-if="isSavingPath" small class="mr-25" />
                              <feather-icon
                                v-else
                                icon="SaveIcon"
                                size="14"
                                class="mr-25"
                              />
                              <span>{{ isSavingPath ? 'Saving...' : 'Save' }}</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-form>
                    </validation-observer>






                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  getCurrentInstance,
  watch,
} from '@vue/composition-api';
import { ValidationObserver } from 'vee-validate';
import { useToast } from 'vue-toastification/composition';
import formValidation from '@core/comp-functions/forms/form-validation';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import store from '@/store';
import Swal from 'sweetalert2';
import PathTab from './PathTab.vue';
import AdvancedTab from './AdvancedTab.vue';
import {
  getDefaultPathValues,
  mapPathFromBackend,
  mapPathToBackend,
  mapPathToTemplate,
  COMM_TYPE_TABLE_LABELS,
  PATH_TYPE_TABLE_LABELS,
} from './useCommPathConfig';
import { useTemplateMode } from '../useTemplateMode';

export default {
  name: 'ConfigCommunicationPaths',
  components: {
    ValidationObserver,
    PathTab,
    AdvancedTab,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    communicationPathsData: {
      type: Array,
      default: () => [],
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    panelAccount: {
      type: [String, Number],
      default: null,
    },
    source: {
      type: String,
      default: 'default',
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const toast = useToast();
    const { refFormObserver } = formValidation();
    const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);

    const isLoading = computed(() => props.loading);
    const isSavingPath = ref(false);
    const isDeletingPath = ref(false);
    const communicationPaths = ref([]);

    const pathFormObserver = ref(null);
    const isEditingPath = ref(false);
    const selectedPath = ref(null);
    const activeTab = ref(0);

    const pathForm = ref(getDefaultPathValues());

    const pathTableFields = [
      { key: 'path', label: 'Path', sortable: true },
      { key: 'pathType', label: 'Path Type', sortable: false },
      { key: 'commType', label: 'Comm Type', sortable: false },
    ];

    const loadCommunicationPaths = () => {
      try {
        const backendPaths = Array.isArray(props.communicationPathsData)
          ? props.communicationPathsData
          : [];

        communicationPaths.value = backendPaths.map((p) => {
          const mapped = mapPathFromBackend(p);
          return {
            path: mapped.path,
            pathTypeLabel: PATH_TYPE_TABLE_LABELS[mapped.pathType] || mapped.pathType,
            commTypeLabel: COMM_TYPE_TABLE_LABELS[mapped.communicationType] || mapped.communicationType,
            _raw: mapped,
          };
        });
      } catch (error) {
        console.error('Error loading communication paths:', error);
        communicationPaths.value = [];
      }
    };

    const addNewPath = () => {
      if (communicationPaths.value.length >= 8) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Limit Reached',
            text: 'Maximum 8 paths allowed',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      isEditingPath.value = false;
      activeTab.value = 0;

      const existingPaths = communicationPaths.value
        .map((p) => parseInt(p.path, 10))
        .filter((n) => !isNaN(n))
        .sort((a, b) => a - b);

      let nextPath = 1;
      for (const num of existingPaths) {
        if (num === nextPath) {
          nextPath++;
        } else {
          break;
        }
      }

      selectedPath.value = {
        path: String(nextPath),
        pathTypeLabel: 'P',
        commTypeLabel: 'None',
      };

      pathForm.value = {
        ...getDefaultPathValues(),
        path: String(nextPath),
        accountNumber: props.panelAccount || '',
      };
    };

    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];
        isEditingPath.value = true;
        selectedPath.value = selected;
        activeTab.value = 0;

        if (selected._raw) {
          pathForm.value = { ...selected._raw };
        } else {
          pathForm.value = getDefaultPathValues();
        }
      } else {
        selectedPath.value = null;
        isEditingPath.value = false;
        pathForm.value = getDefaultPathValues();
      }
    };

    const deletePath = async () => {
      if (!isEditingPath.value || !selectedPath.value) return;

      const result = await Swal.fire({
        title: proxy.$t('messages.delete.title'),
        text: proxy.$t('messages.delete.body'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: proxy.$t('actions.delete'),
        cancelButtonText: proxy.$t('actions.cancel'),
        customClass: {
          confirmButton: 'btn btn-primary text-capitalize',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) return;

      if (isTemplateMode.value) {
        const list = (props.communicationPathsData || []).filter(
          (p) =>
            parseInt(p.path || p.pathNumber, 10) !==
            parseInt(pathForm.value.path, 10)
        );
        communicationPaths.value = list.map((p) => {
          const mapped = mapPathFromBackend(p);
          return {
            path: mapped.path,
            pathTypeLabel:
              PATH_TYPE_TABLE_LABELS[mapped.pathType] || mapped.pathType,
            commTypeLabel:
              COMM_TYPE_TABLE_LABELS[mapped.communicationType] ||
              mapped.communicationType,
            _raw: mapped,
          };
        });
        emitTemplateUpdate('communication_paths', list);
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Communication path deleted successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        });
        selectedPath.value = null;
        isEditingPath.value = false;
        pathForm.value = getDefaultPathValues();
        return;
      }

      isDeletingPath.value = true;
      store.commit('dmp_panels/setOperationActive', true);
      try {
        const response = await store.dispatch('dmp_panels/deleteCommunicationPath', {
          uuid: props.uuid,
          pathNumber: pathForm.value.path,
        });

        if (response.success) {
          emit('operation-completed');
          toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: 'Communication path deleted successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
          selectedPath.value = null;
          isEditingPath.value = false;
          pathForm.value = getDefaultPathValues();
        } else {
          throw new Error(response.message || 'Failed to delete communication path');
        }
      } catch (error) {
        console.error('Error deleting communication path:', error);
        emit('operation-error', error);
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to delete communication path',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        isDeletingPath.value = false;
        store.commit('dmp_panels/setOperationActive', false);
      }
    };

    const savePath = async () => {
      const isValid = await pathFormObserver.value?.validate();
      if (!isValid) return;

      if (isTemplateMode.value) {
        const list = [...(props.communicationPathsData || [])];
        const idx = list.findIndex(
          (p) =>
            parseInt(p.path || p.pathNumber, 10) ===
            parseInt(pathForm.value.path, 10)
        );
        const original = idx !== -1 ? list[idx] : {};
        const item = mapPathToTemplate(pathForm.value, original);
        if (idx !== -1) list.splice(idx, 1, item);
        else list.push(item);

        communicationPaths.value = list.map((p) => {
          const mapped = mapPathFromBackend(p);
          return {
            path: mapped.path,
            pathTypeLabel:
              PATH_TYPE_TABLE_LABELS[mapped.pathType] || mapped.pathType,
            commTypeLabel:
              COMM_TYPE_TABLE_LABELS[mapped.communicationType] ||
              mapped.communicationType,
            _raw: mapped,
          };
        });
        emitTemplateUpdate('communication_paths', list);
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: isEditingPath.value
              ? 'Communication path updated successfully'
              : 'Communication path created successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        });
        selectedPath.value = null;
        isEditingPath.value = false;
        pathForm.value = getDefaultPathValues();
        return;
      }

      isSavingPath.value = true;
      store.commit('dmp_panels/setOperationActive', true);

      try {
        const pathToSave = mapPathToBackend(pathForm.value);

        const response = await store.dispatch('dmp_panels/setCommunicationPath', {
          uuid: props.uuid,
          pathData: pathToSave,
        });

        if (response.success) {
          emit('operation-completed');
          toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: isEditingPath.value
                ? 'Communication path updated successfully'
                : 'Communication path created successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
          selectedPath.value = null;
          isEditingPath.value = false;
          pathForm.value = getDefaultPathValues();
        } else {
          throw new Error(response.message || 'Failed to save communication path');
        }
      } catch (error) {
        console.error('Error saving communication path:', error);
        emit('operation-error', error);
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to save communication path',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        isSavingPath.value = false;
        store.commit('dmp_panels/setOperationActive', false);
      }
    };

    const onActivateCellular = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Cellular Activation',
          text: 'Endpoint de activación pendiente por backend',
          icon: 'InfoIcon',
          variant: 'info',
        },
      });
    };

    onMounted(() => {
      loadCommunicationPaths();
    });

    watch(
      () => props.uuid,
      (newUuid) => {
        if (newUuid) {
          loadCommunicationPaths();
        }
      }
    );

    watch(
      () => props.communicationPathsData,
      (newData) => {
        if (newData) {
          loadCommunicationPaths();
        }
      },
      { deep: true }
    );

    return {
      communicationPaths,
      isLoading,
      isSavingPath,
      isDeletingPath,
      pathForm,
      pathFormObserver,
      isEditingPath,
      pathTableFields,
      selectedPath,
      activeTab,

      addNewPath,
      deletePath,
      savePath,
      onRowSelected,
      onActivateCellular,

      refFormObserver,
    };
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables/_variables.scss';

.card-header-custom {
  background-color: transparent;
}

.table-column,
.details-column {
  transition: all 0.3s ease-in-out;
}

.details-column {
  .card {
    position: sticky;
    top: 5rem;
  }
}

.comm-path-tabs {
  ::v-deep .nav-pills {
    .nav-link {
      padding: 0.5rem 1rem;
    }
  }
}

.dark-layout {
  .card-header-custom {
    background-color: transparent !important;
  }
}

.gap-1 {
  gap: 0.25rem;
}
</style>
